/**
 * AdminPeerHandler - Implements PeerTypeHandler for admin session peers.
 *
 * Replaces the legacy AdminSessionManager. Manages admin session lifecycle:
 * input filter toggling, elevated PowerShell launch, idle timer with
 * warning/kick, session end notification, and reboot scheduling.
 * Chunked elevated PowerShell script delivery is delegated to AdminPowerShellScriptManager.
 *
 * Registered with PeerTypeRegistry for the 'admin' peer type.
 * Called by InputManager for admin control messages (adminSessionExit).
 * Called by realtime WebSocket handler for ADMIN_SESSION_END messages.
 */

import * as Sentry from '@sentry/react';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import { ADMIN_SESSION_IDLE_TIMEOUT_MS, ADMIN_SESSION_IDLE_WARNING_MS, generators, websocket, AdminMessageType,
  type AdminSessionExitAction } from '@playcastdotio/Messaging';
import type { PeerTypeHandler, SerializableMessage } from '../types';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { AppCoordinator } from '../../app/AppCoordinator';
import type { PeerCoordinator } from '../PeerCoordinator';
import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import { hostInputDominanceState, hostMatchmakingState, machineIdState } from '../../../api/stores/shared'; // Accepted exception per user guidance
import { setSuppressHostDominanceKick } from '../../../api/modules/events/adminConnectGuard';
import { AdminPowerShellScriptManager } from '../managers/AdminPowerShellScriptManager';
import { AdminElevatedProcessManager } from '../managers/AdminElevatedProcessManager';
import type { RecordingControlManager } from '../../../services/playjector/managers/RecordingControlManager';
import type { LocalHostSessionCoordinator } from '../../local-host-session/LocalHostSessionCoordinator';
import type { MacroRecorderCaptureSourceManager } from '../../../services/playjector/managers/MacroRecorderCaptureSourceManager';
import { AdminMacroRecorderRouter, isMacroRecorderMessage } from './AdminMacroRecorderRouter';
import { AdminSessionExitPolicy } from './AdminSessionExitPolicy';
import { AdminPeerSessionManager } from '../managers/AdminPeerSessionManager';
import { LocalHostPeerBoundary } from '../../local-host-session/LocalHostPeerBoundary';

export type AdminSessionDisconnectReason = 'admin_exit' | 'timeout' | 'unexpected';

export type AdminPeerHandlerConfig = {
  idleTimeout: number;
  idleWarning: number;
};

const DEFAULT_CONFIG: AdminPeerHandlerConfig = {
  idleTimeout: ADMIN_SESSION_IDLE_TIMEOUT_MS,
  idleWarning: ADMIN_SESSION_IDLE_WARNING_MS,
};

export interface ISessionPresenceUpdater {
  setAdminSessionActive(
    active: boolean,
    identity?: { sessionId?: string; peerId?: string },
  ): void;
  removeHostingStatus(): void;
}

export type AdminPeerHandlerDeps = {
  ipcService: IIpcService;
  realtimeService: IRealtimeService;
  appCoordinator: AppCoordinator;
  peerCoordinator: PeerCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  sessionPresenceUpdater: ISessionPresenceUpdater;
  recordingControlManager?: RecordingControlManager;
  captureSourceManager?: MacroRecorderCaptureSourceManager;
  localHostSession?: LocalHostSessionCoordinator;
};

export class AdminPeerHandler implements PeerTypeHandler {
  private readonly sessionIdentity = new AdminPeerSessionManager();
  private isAdminSession = false;
  private adminPeerId: string | null = null;
  /** Redis reservation UUID from peer metadata. Distinct from `adminPeerId`. */
  private adminReservationId: string | null = null;
  private adminDisconnectReason: AdminSessionDisconnectReason = 'unexpected';
  private adminRebootRequested = false;
  private adminExitAction: AdminSessionExitAction = 'keep_game';
  /**
   * Recorder purpose enables its control messages. All implicit admin exits
   * preserve the game and Host mode; destructive exits require an explicit choice.
   */
  private isMacroRecorderSession = false;
  private readonly elevatedProcesses: AdminElevatedProcessManager;
  private readonly psScriptManager: AdminPowerShellScriptManager;
  private readonly macroRecorder: AdminMacroRecorderRouter;
  private readonly localPeers: LocalHostPeerBoundary;
  private idleWarningTimer: ReturnType<typeof setTimeout> | null = null;
  private idleKickTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly deps: AdminPeerHandlerDeps,
    private readonly config: AdminPeerHandlerConfig = DEFAULT_CONFIG,
  ) {
    this.localPeers = new LocalHostPeerBoundary(deps.peerCoordinator, deps.localHostSession);
    this.elevatedProcesses = new AdminElevatedProcessManager(deps.ipcService, deps.appCoordinator);
    this.psScriptManager = new AdminPowerShellScriptManager({
      ipcService: deps.ipcService,
      appCoordinator: deps.appCoordinator,
      notifyActivity: () => this.resetIdleTimer(),
    });
    this.macroRecorder = new AdminMacroRecorderRouter({
      peerCoordinator: deps.peerCoordinator,
      recordingControlManager: deps.recordingControlManager,
      captureSourceManager: deps.captureSourceManager,
      localHostSession: deps.localHostSession,
    });
  }

  // ========================================
  // PeerTypeHandler interface
  // ========================================

  public onConnected(peerId: string, metadata: PeerMetadata): void {
    if (this.localPeers.connected(peerId)) return;
    console.log('[AdminPeerHandler] Admin peer connected:', peerId);

    this.isAdminSession = true;
    this.adminPeerId = peerId;
    const identity = this.sessionIdentity.connect(peerId, metadata);
    this.adminReservationId = identity?.sessionId
      ?? (typeof metadata?.adminSessionId === 'string' ? metadata.adminSessionId : null);
    this.adminDisconnectReason = 'unexpected';
    this.adminRebootRequested = false;
    // A browser crash, reload or lost connection preserves the running game.
    // Explicit completion choices can still request restart, exit or reboot.
    this.isMacroRecorderSession = metadata?.purpose === 'macroRecorder';
    this.adminExitAction = 'keep_game';
    if (this.isMacroRecorderSession) {
      console.log('[AdminPeerHandler] Macro-recorder session — abrupt disconnect will retain game and Nonsole Mode');
    }

    const hostId = this.deps.appCoordinator.getHostAuthId();

    // PeerCoordinator already armed adminSession (toggleFilters) and bound Cam03 before
    // the SDP answer. Resync filters only (idempotent) — a second getSources here would
    // race the cold-bind / gUM handshake.
    this.deps.ipcService.send(
      generators.input.toggleFilters(hostId, { keyboard: false, mouse: false, gamepad: false, touch: false }),
    );
    console.log('[AdminPeerHandler] Input filtering resynced for admin session (arm owned by PeerCoordinator)');

    // Suppress HOST_DOMINANCE TS kick only while the admin peer is connected.
    setSuppressHostDominanceKick(true);

    this.deps.pavilionCoordinator.notifyAdminSessionChanged(true);
    console.log('[AdminPeerHandler] Matchmaking recalculated for admin session start');

    this.deps.sessionPresenceUpdater.setAdminSessionActive(
      true,
      this.adminReservationId ? { sessionId: this.adminReservationId, peerId } : undefined,
    );

    this.startIdleTimer();
  }

  public onDisconnected(peerId: string): void {
    if (this.localPeers.disconnected(peerId)) return;
    if (!this.isAdminSession || this.adminPeerId !== peerId) return;
    this.sessionIdentity.release(peerId);
    this.deps.localHostSession?.releaseRecorderInput?.(peerId);

    console.log('[AdminPeerHandler] Admin peer disconnected:', peerId, 'reason:', this.adminDisconnectReason);

    const hostId = this.deps.appCoordinator.getHostAuthId();
    const exitAction = this.adminExitAction;
    const exitGuestMode = exitAction === 'reboot' || exitAction === 'exit_nonsole';
    const shouldReboot = exitAction === 'reboot';

    this.deps.ipcService.send(
      generators.input.toggleFilters(hostId, {
        keyboard: true,
        mouse: true,
        gamepad: true,
        touch: true,
        exitGuestModeAfterAdminSession: exitGuestMode,
        rebootIfSafeAfterGuestModeExit: shouldReboot,
      }),
    );

    // Clear stale dominance so the next physical key/mouse edge triggers host hard-kick again (Playjector
    // reset alone did not always sync host UI state before InputThread.cpp fix; this covers older builds too).
    hostInputDominanceState.updateState({ keyboard: false, mouse: false });
    setSuppressHostDominanceKick(false);

    const guestAccount = hostMatchmakingState.currentState.guestAccount;
    if (guestAccount) {
      this.deps.ipcService.send(
        generators.process.processSetPolicy(
          hostId,
          true,
          false,
          true,
          true,
          60000,
          true,
          true,
          true,
          true,
          true,
        ),
      );
    }

    this.clearIdleTimer();

    if (exitAction === 'restart_game') {
      void this.deps.pavilionCoordinator.restartGameForAdminExit().catch((err) => {
        console.error('[AdminPeerHandler] restartGameForAdminExit failed:', err);
      });
    } else if (exitGuestMode) {
      // Destructive exits (reboot / exit Nonsole) tear the machine down. Force
      // Pavilion into a terminal non-matchmakeable state so it cannot re-enter
      // the pool in the window before PlaycastService completes the guest-mode
      // exit / reboot. This also stops the broadcast (removes hosting status).
      this.deps.pavilionCoordinator.enterTerminalExitState(exitAction);
    } else {
      // keep_game: stop the current stream but let the host re-enter matchmaking
      // so the next player can connect to the still-running game.
      this.deps.sessionPresenceUpdater.removeHostingStatus();
    }

    const machineId = machineIdState.currentState;
    this.deps.realtimeService.sendMessage(
      websocket.generators.admin(AdminMessageType.ADMIN_SESSION_END, {
        sessionId: this.adminReservationId ?? peerId,
        machineId,
        reason: this.adminDisconnectReason,
      }),
    );

    this.isAdminSession = false;
    this.adminPeerId = null;
    this.adminReservationId = null;
    this.adminDisconnectReason = 'unexpected';
    this.adminRebootRequested = false;
    this.adminExitAction = 'keep_game';
    this.psScriptManager.clearTransfers();

    this.deps.pavilionCoordinator.notifyAdminSessionChanged(false);
    this.deps.sessionPresenceUpdater.setAdminSessionActive(false);
    console.log('[AdminPeerHandler] Matchmaking recalculated after admin session end');

    if (shouldReboot) {
      console.log(
        '[AdminPeerHandler] Reboot-after-admin requested via toggleFilters (PlaycastService exits guest mode first, then reboots if no user sessions remain)',
      );
    } else if (exitAction === 'restart_game') {
      console.log('[AdminPeerHandler] Admin exit restart_game — game relaunch requested, Nonsole Mode retained');
    } else if (exitAction === 'keep_game') {
      console.log('[AdminPeerHandler] Admin exit keep_game — game and Nonsole Mode retained');
    }
  }

  public onMessage(peerId: string, _message: SerializableMessage): void {
    if (this.localPeers.blocksControl(peerId)) return;
    if (this.adminPeerId === peerId) {
      this.resetIdleTimer();
    }
  }

  // ========================================
  // Admin control messages (from data channel via InputManager)
  // ========================================

  public handleControlMessage(peerId: string, body: unknown, headerAction?: string): void {
    if (this.localPeers.blocksControl(peerId)) return;
    if (!this.isAdminSession || this.adminPeerId !== peerId) return;

    const msg = body as Record<string, unknown> | undefined;
    if (!msg) {
      Sentry.logger.warn('[AdminPeerHandler] Received admin control message with no body');
      return;
    }

    if (msg.adminSessionExit === true) {
      console.log('[AdminPeerHandler] Received adminSessionExit from admin peer');
      this.adminDisconnectReason = 'admin_exit';
      this.adminExitAction = AdminSessionExitPolicy.parse(msg, this.adminExitAction);
      this.adminRebootRequested = this.adminExitAction === 'reboot';
      if (this.adminRebootRequested) {
        console.log('[AdminPeerHandler] Reboot requested with admin exit');
      }
      return;
    }

    if (msg.powerShellScriptChunk === true) {
      this.psScriptManager.handleScriptChunk(msg);
      return;
    }

    if (msg.restorePlaycast === true) {
      if (this.elevatedProcesses.restorePlaycast()) this.resetIdleTimer();
      return;
    }

    if (msg.launchElevatedProcess === true) {
      if (this.elevatedProcesses.launch(msg)) this.resetIdleTimer();
      return;
    }

    // macroRecorder* messages — route to RecordingControlManager
    if (isMacroRecorderMessage(msg, headerAction)) {
      this.handleMacroRecorderMessage(peerId, msg, headerAction);
      return;
    }

    this.resetIdleTimer();
  }

  // ========================================
  // Macro recorder message routing (AdminMacroRecorderRouter)
  // ========================================

  private handleMacroRecorderMessage(peerId: string, msg: Record<string, unknown>, headerAction?: string): void {
    this.resetIdleTimer();

    // Detect recorder purpose when older peers omit its metadata, without
    // changing a completion action that the admin has already chosen.
    if (!this.isMacroRecorderSession) {
      this.isMacroRecorderSession = true;
      console.log('[AdminPeerHandler] Detected macro-recorder session via control message');
    }
    this.macroRecorder.handle(peerId, msg, headerAction);
  }

  /**
   * Send a fire-and-forget notification to the admin peer (not a reply).
   * Used to push upload status updates to admin without a preceding request.
   */
  public sendUploadStatusNotification(status: {
    takeId: string;
    status: 'started' | 'progress' | 'complete' | 'failed';
    progressPercent?: number;
    error?: string;
    videoKey?: string;
  }): void {
    this.deps.localHostSession?.sendRecorderStatus(status);
    if (!this.isAdminSession || !this.adminPeerId) return;

    const payload = JSON.stringify({
      header: { target: 'system', action: 'macroRecorderUploadStatus', isReply: false, source: 'host' },
      body: { message: status },
    });
    this.deps.peerCoordinator.sendToPeer(this.adminPeerId, payload);
    Sentry.logger.info('[AdminPeerHandler] macro-recorder upload status notification sent', {
      feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
      takeId: status.takeId, uploadStatus: status.status,
    });
  }

  // ========================================
  // Admin WebSocket messages (from realtime handler)
  // ========================================

  public handleAdminSessionMessage(message: { type: string; [key: string]: unknown }): void {
    if (message.type !== AdminMessageType.ADMIN_SESSION_END) return;
    if (!this.isAdminSession || !this.adminPeerId) return;

    // Force-recovery addresses the live peer id. Dashboard and disconnect
    // cleanups address the reservation UUID. A different id belongs to a
    // session that is already gone, so it must not kick the current peer.
    // Messages with no id stay applicable (older servers omit it).
    const sessionId = typeof message.sessionId === 'string' ? message.sessionId : undefined;
    if (
      sessionId
      && sessionId !== this.adminPeerId
      && sessionId !== this.adminReservationId
    ) {
      console.log('[AdminPeerHandler] Ignoring ADMIN_SESSION_END for a different session:', sessionId);
      return;
    }

    const { reboot, exitAction } = message as { reboot?: boolean; exitAction?: AdminSessionExitAction; type: string };
    // A bare server ADMIN_SESSION_END (no exitAction) must not clobber the
    // session's safe default or an explicit action already chosen by the admin.
    const parsedExitAction = AdminSessionExitPolicy.parse({ exitAction, reboot }, this.adminExitAction);
    console.log(`[AdminPeerHandler] Received ADMIN_SESSION_END from server (reboot=${!!reboot}, exitAction=${parsedExitAction})`);

    this.adminExitAction = parsedExitAction;
    if (parsedExitAction === 'reboot' || reboot) {
      this.adminRebootRequested = true;
    }

    if (this.adminDisconnectReason === 'unexpected') {
      this.adminDisconnectReason = 'admin_exit';
    }

    const kickReason = parsedExitAction === 'reboot' || reboot
      ? 'admin_session_end:reboot'
      : 'admin_session_end:server';
    this.deps.peerCoordinator.kickPeer(this.adminPeerId, kickReason);
  }

  // ========================================
  // Public getters
  // ========================================

  public getIsAdminSession(): boolean {
    return this.isAdminSession;
  }

  public getAdminPeerId(): string | null {
    return this.adminPeerId;
  }
  public ownsSession(sessionId: string): boolean {
    return this.isAdminSession && this.sessionIdentity.owns(sessionId, this.adminPeerId);
  }
  public onSessionReleased(listener: (sessionId: string) => void): () => void {
    return this.sessionIdentity.onReleased(listener);
  }

  // ========================================
  // Idle timer
  // ========================================

  public resetIdleTimer(): void {
    if (this.isAdminSession && this.adminPeerId) {
      this.startIdleTimer();
    }
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();

    this.idleWarningTimer = setTimeout(() => {
      if (!this.isAdminSession || !this.adminPeerId) return;
      console.warn('[AdminPeerHandler] Admin idle warning — 1 minute remaining');

      const remainingMs = this.config.idleTimeout - this.config.idleWarning;
      this.deps.realtimeService.sendMessage(
        websocket.generators.admin(AdminMessageType.ADMIN_SESSION_TIMEOUT_WARNING, {
          sessionId: this.adminReservationId ?? this.adminPeerId,
          remainingSeconds: Math.round(remainingMs / 1000),
        }),
      );
    }, this.config.idleWarning);

    this.idleKickTimer = setTimeout(() => {
      if (!this.isAdminSession || !this.adminPeerId) return;
      console.warn('[AdminPeerHandler] Admin idle timeout expired — kicking admin peer');
      this.adminDisconnectReason = 'timeout';
      this.deps.peerCoordinator.kickPeer(this.adminPeerId, 'kicked:admin_idle');
    }, this.config.idleTimeout);
  }

  private clearIdleTimer(): void {
    if (this.idleWarningTimer) {
      clearTimeout(this.idleWarningTimer);
      this.idleWarningTimer = null;
    }
    if (this.idleKickTimer) {
      clearTimeout(this.idleKickTimer);
      this.idleKickTimer = null;
    }
  }
}
